import { useEffect, useRef, useState } from 'react';
import { AppModal } from '@/components/compose/app.modal';
import StepIndicator from 'react-native-step-indicator';
import styled, { useTheme } from 'styled-components/native';
import { useUser } from '@/hooks/use.user';
import { useAssets } from '@/hooks/use.assets';
import { AppButton } from '@/components/base/app.button';
import { Box } from '@/components/base/box';
import { useScreenSize } from '@/hooks/use.screen.size';
import { AppTextView } from '@/components/base/app.text.view';
import { AppImageView } from '@/components/base/app.image.view';
import { AppTextField } from '@/components/base/app.text.field';
import { useAppDispatch } from '@/hooks/use.redux';
import { actionSetVisitSurvey } from '@/redux';
import { useCancelSurvey, useSubmitSurvey } from '@/apis/surveys';
import { useAppRouter } from '@/hooks/use.app.router';
import { hpVal } from '../../utils';

export default function PatientSurveyModal() {
  const {
    userState: { pendingSurvey },
  } = useUser();

  const visit = pendingSurvey;
  const successRef = useRef<boolean>(false);
  const navigation = useAppRouter();
  const dispatch = useAppDispatch();

  const assets = useAssets();
  const { mutate: cancelSurvey } = useCancelSurvey({
    onSuccess: (data) => {
      successRef.current = true;
      dispatch(actionSetVisitSurvey(data));
    },
  });
  const { mutate: send, isPending } = useSubmitSurvey({
    onSuccess: (data) => {
      successRef.current = true;
      navigation.goBack();
      dispatch(actionSetVisitSurvey(data));
    },
  });
  const [serviceQuality, setServiceQuality] = useState(0);
  const [mediaQuality, setMediaQuality] = useState(0);
  const [doctorQuality, setDoctorQuality] = useState(0);
  const [message, setMessage] = useState('');

  useEffect(() => {
    return () => {
      if (!successRef.current && visit) {
        cancelSurvey({ params: { id: visit._id } });
      }
    };
  }, []);

  if (!visit) {
    return null;
  }

  return (
    <AppModal title={`تجربه مشاوره با ${visit.doctor?.name || '(کاربر حذف شده)'}`}>
      <Box fullWidth alignItems={'center'}>
        <Image source={assets.images.survey} />
        <Row text={'از اپلیکیشن مطپ چقدر رضایت دارید؟'} stepPosition={serviceQuality} onStepChange={setServiceQuality} />
        <Row text={'رضایت شما از کیفیت صدا و تصویر چقدر بود؟'} stepPosition={mediaQuality} onStepChange={setMediaQuality} />
        <Row text={'از پزشک خود چقدر راضی بودید؟'} stepPosition={doctorQuality} onStepChange={setDoctorQuality} />
        <SurveyTextField autoGrow value={message} onChangeText={setMessage} placeholder={'پیام شما'} />
        <SubmitButton
          title={'ارسال'}
          loading={isPending}
          onPress={() =>
            send({
              params: {
                id: visit._id,
              },
              body: {
                appServiceQuality: serviceQuality,
                doctorQuality: doctorQuality,
                videoCallQuality: mediaQuality,
                extraMessage: message,
              },
            })
          }
        />
      </Box>
    </AppModal>
  );
}

function Row({ text, stepPosition, onStepChange }: { text: string; stepPosition: number; onStepChange: (position: number) => void }) {
  const { min } = useScreenSize();
  const theme = useTheme();
  return (
    <RowContainer fullWidth>
      <RowText text={text} textColor={'blue'} />
      <StepsBox fullWidth>
        <StepIndicator
          currentPosition={stepPosition}
          labels={['ضعیف', 'متوسط', 'خوب', 'عالی']}
          onPress={(step) => onStepChange(step)}
          stepCount={4}
          renderStepIndicator={() => <></>}
          customStyles={{
            stepIndicatorSize: min(2.5),
            currentStepIndicatorSize: min(2.5),
            separatorStrokeWidth: min(0.7),
            currentStepStrokeWidth: 0,
            stepStrokeCurrentColor: 'white',
            stepStrokeWidth: 0,
            separatorFinishedColor: theme.colors.jade,
            separatorUnFinishedColor: theme.colors.jadeVeryLight,
            stepIndicatorFinishedColor: theme.colors.jade,
            stepIndicatorUnFinishedColor: theme.colors.jadeVeryLight,
            stepIndicatorCurrentColor: theme.colors.jade,
            labelColor: theme.colors.jade,
            labelSize: theme.fontSizes.text.primary,
            currentStepLabelColor: theme.colors.jade,
            labelFontFamily: theme.fonts.primary,
          }}
        />
      </StepsBox>
    </RowContainer>
  );
}

const Image = styled(AppImageView)`
  width: 60px;
  height: 60px;
  margin-bottom: ${hpVal(2)};
`;

const SurveyTextField = styled(AppTextField)`
  width: 80%;
  margin-bottom: ${hpVal(2)};
`;

const SubmitButton = styled(AppButton)`
  width: 100%;
`;

const RowContainer = styled(Box)`
  margin-bottom: ${hpVal(2.5)};
  align-items: center;
  width: 100%;
`;

const RowText = styled(AppTextView)`
  margin-bottom: ${hpVal(2)};
`;

const StepsBox = styled(Box)`
  width: 100%;
`;
